import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  width?: 'narrow' | 'normal' | 'wide';
}

const widthClasses = {
  narrow: 'max-w-xs',   // 320px
  normal: 'max-w-md',   // 448px
  wide: 'max-w-lg',     // 512px
};

export function SidePanel({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  showCloseButton = true,
  width = 'normal'
}: SidePanelProps) {
  
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel Container */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className={`pointer-events-auto w-screen ${widthClasses[width]} sm:max-w-md md:${widthClasses[width]}`}>
                  <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-6 border-b border-gray-200 dark:border-gray-700 sm:px-6">
                      <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {title}
                      </Dialog.Title>
                      
                      {showCloseButton && (
                        <button
                          type="button"
                          className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
                          onClick={onClose}
                          aria-label="Close panel"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    {/* Content */}
                    <div className="relative flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                      {children}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// Convenience wrapper for settings/preferences panels
interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  onReset?: () => void;
  title: string;
  children: React.ReactNode;
  showActionButtons?: boolean;
  saveText?: string;
  resetText?: string;
  hasUnsavedChanges?: boolean;
}

export function SettingsPanel({
  isOpen,
  onClose,
  onSave,
  onReset,
  title,
  children,
  showActionButtons = true,
  saveText = 'Save',
  resetText = 'Reset',
  hasUnsavedChanges = false
}: SettingsPanelProps) {
  
  const handleSave = () => {
    onSave?.();
    onClose();
  };

  const handleReset = () => {
    onReset?.();
  };

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={title} width="normal">
      <div className="flex flex-col h-full">
        {/* Settings Content */}
        <div className="flex-1 pb-6">
          {children}
        </div>

        {/* Action Buttons */}
        {showActionButtons && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex gap-3 justify-end">
              {onReset && (
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
                  onClick={handleReset}
                >
                  {resetText}
                </button>
              )}
              
              {onSave && (
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors ${
                    hasUnsavedChanges 
                      ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' 
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges}
                >
                  {saveText}
                </button>
              )}
              
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  );
}